import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { Theme } from 'constants/enums';

const STORAGE_KEY = 'theme';

interface ThemeContextType {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
}

interface ThemeProviderProps {
    userTheme: Theme | null;
    children: React.ReactNode;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const isTheme = (value: unknown): value is Theme =>
    value === Theme.light || value === Theme.dark;

const resolveInitialTheme = (userTheme: Theme | null): Theme => {
    if (typeof window === 'undefined') return userTheme ?? Theme.dark;

    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isTheme(stored)) return stored;

    if (userTheme) return userTheme;

    return Theme.dark;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ userTheme, children }) => {
    const [theme, setTheme] = useState<Theme>(() => resolveInitialTheme(userTheme));

    useLayoutEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    useEffect(() => {
        window.localStorage.setItem(STORAGE_KEY, theme);
    }, [theme]);

    const toggleTheme = () =>
        setTheme(current => (current === Theme.dark ? Theme.light : Theme.dark));

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};